import { cloneDeep, upperFirst, lowerFirst } from 'lodash';
import { Tab } from 'utils';
import {
    Localization,
    SelectionCategory,
    SelectionHeritage,
    SelectionHighlighted,
    SelectionPlotlyLegend,
    SelectionScenarioSex,
} from './enums';

export const DEFAULT_PLOT_HEIGHT = 520;
export const DEFAULT_PLOT_RANGE = [2024, 2040];
export const DEFAULT_PLOT_RANGE_UK = [2025, 2060];
export const DEFAULT_SINGLE_SLIDE_RANGE = [2025, 2040];

export const initialPlotDataOptimization = (objectOfJSON: any) => {
    const result: any = {};
    for (const key in objectOfJSON) {
        result[key] = {
            ...objectOfJSON[key],
            data: objectOfJSON[key].data.map((el: any) => ({
                ...el,
                visible: true,
            })),
        };
    }

    return result;
};

export const snakeCaseToRegular = (name: string) => {
    return name?.replaceAll('_', ' ');
};

export const normalizeString = (str: string) => {
    return str?.replaceAll(/[ ,+\-&><$]/g, '').toUpperCase();
};

export const fileKeyParser = (object: any, objectKey?: string) => {
    let result = '';
    if (objectKey === 'Category Spend') {
        result += object['ageSex'];
        result += object['dropDownHeritage'];
        result += `_${object['perAnnum']}`;
    } else if (objectKey === 'Drivers of Growth') {
        result = `${object['dropDownAllCategories']}_${object['year']}`;
    } else {
        for (const key in object) {
            result += object[key];
        }
    }

    return result;
};

export const checkboxDataGenerator = (currentData: any, tabName?: string) => {
    const preparedData = currentData
        .filter((elem: any) => elem)
        .reduce((acc: any, elem: any) => {
            return [...acc, ...elem.data];
        }, []);
    const result = Object.values(
        preparedData
            .filter(
                (el: any) =>
                    el.visible !== undefined &&
                    el.name !== SelectionHighlighted.DEMOGRAPHIC_POPULATION &&
                    el.name !== SelectionPlotlyLegend.CATEGORY_SPEND_CUMULATIVE &&
                    el.name !== SelectionPlotlyLegend.CATEGORY_SPEND_PER_ANNUM &&
                    el.name,
            )
            .reduce((acc: any, obj: any) => ({ ...acc, [obj.name]: obj }), {}),
    );

    if (tabName === 'Demographic') {
        return result;
    }
    return result;
};

export const capitalizeFirstLetter = (letter: string) => {
    let incomingText = letter;
    if (incomingText === 'age_grp') {
        incomingText = 'age group';
    }
    return incomingText.charAt(0).toUpperCase() + incomingText.slice(1);
};

export const marksForSlider = (min: number, max: number) => {
    const initialArray = Array.from(Array(max - min).keys());
    const resultArray = initialArray.map((el: any, index: number) => ({
        value: min + index,
    }));

    return resultArray;
};

export const plotHeightDetector = (tabName: string, length: number, type?: string) => {
    if (tabName === 'subcategory') {
        if (length <= 3) {
            return DEFAULT_PLOT_HEIGHT;
        } else if (length > 3 && length <= 5) {
            return 900;
        } else if (length > 4) {
            return 1240;
        }
    } else if (type === 'Age' && tabName === 'Category Spend') {
        return 1020;
    } else {
        return DEFAULT_PLOT_HEIGHT;
    }
};

export const sideModalTitleChanger = (title: string) => {
    switch (title) {
        case 'None':
            return 'Total defined segment';
        case 'sex':
            return 'Sex';
        case 'age_grp':
            return 'Age groups';
        case 'heritage':
            return 'Ethnic Groups';
        case 'category':
            return 'Categоries';
        default:
            return title;
    }
};

export const legendTitleChanger = (title: string) => {
    switch (title) {
        case 'heritage':
            return 'Ethnic group';
        case 'None':
            return 'Total defined segment';
        default:
            return title;
    }
};

export const initialPlotVisibility = (name: string) => {
    if (
        name === SelectionPlotlyLegend.RAPID_SOCIOECONOMIC ||
        name === SelectionPlotlyLegend.PERSISTENT_SOCIOECONOMIC ||
        name === SelectionPlotlyLegend.NO_SOCIOECONOMIC ||
        name === SelectionPlotlyLegend.RESTRICTED_SOCIOECONOMIC
    ) {
        return false;
    }

    return true;
};

export const requestFormatter = (data: any) => {
    const RequestSchema: any = {
        [SelectionHeritage.BLACK]: 'heritage_black',
        [SelectionHeritage.WHITE]: 'heritage_white',
        [SelectionScenarioSex.FEMALES]: 'F',
        [SelectionScenarioSex.MALES]: 'M',
    };

    return data.map((el: string) => RequestSchema[el]);
};

export const plotlyLineColorization = (
    name: string,
    array: any[],
    pallette1: any,
    element: any,
    DEFAULT_LINE_COLOR: string,
    DEFAULT_LINE_WIDTH: number,
    DEFAULT_NONE_COLOR: string,
    isNoneCase?: boolean,
) => {
    const withWhiteDividers = [
        'Demographic',
        'sex',
        'Sex',
        'age_grp',
        'Age group',
        'heritage',
        'Ethnic group',
        'Category',
        'Category Spend',
        SelectionCategory.CONSUMER_ELECTRONICS,
        SelectionCategory.GROCERY,
        SelectionCategory.NON_ALCOHOLIC_BEVERAGES,
        SelectionCategory.INSURANCE,
        SelectionCategory.ALCOGOLIC_BEVERAGES,
    ];

    let line = { color: '', width: 1 };
    if (array.length === 2 && !element['fillcolor']) {
        line.color = DEFAULT_NONE_COLOR;
        line.width = 3;
    } else {
        const withNonCasesLine = isNoneCase ? DEFAULT_NONE_COLOR : DEFAULT_LINE_COLOR;
        const withNonCasesPallette = isNoneCase ? DEFAULT_NONE_COLOR : pallette1[element.name];
        line.color =
            withWhiteDividers.includes(name) ||
            (name === 'subcategory' && array.length > 2) ||
            (name === 'category' && array.length > 2)
                ? withNonCasesLine
                : withNonCasesPallette;
        line.width = withWhiteDividers.includes(name) ? DEFAULT_LINE_WIDTH : 3;
    }

    return line;
};

export const customTitleGenerator = (isCustom: boolean, title: string) => {
    if (isCustom) {
        switch (true) {
            case title === 'Current Spend':
                return 'Individual Disposable Income';
            default:
                return title;
        }
    } else {
        return title;
    }
};

export const getAmountOfPlots = (layout: any) => {
    let counter = 1;
    if (layout?.['annotations']) {
        counter = layout['annotations']?.length;
    }
    return Math.ceil(counter / 2) * DEFAULT_PLOT_HEIGHT;
};

export function optimizeScreenName(str: string) {
    const loweredString = str.toLocaleLowerCase();
    return loweredString.charAt(0).toUpperCase() + loweredString.slice(1);
}

export function updateAxis(plot: any, range: number[], title: string, isCaseValid: boolean) {
    let plotObject = cloneDeep(plot);
    for (let key in plotObject.layout) {
        if (key.includes('xaxis') && title !== Tab.DRIVERS_OF_GROWTH) {
            plotObject.layout[key].range = range;
            plotObject.layout[key].tickvals = range;
            plotObject.layout[key].tick0 = range[0];
        }
    }
    if (isCaseValid) {
        return plotObject;
    } else {
        return plot;
    }
}

export const dynamicCheckboxVisibility = (dataState: any, name: string) => {
    return dataState.map((prevData: any) => ({
        ...prevData,
        data: prevData.data.map((dataSegement: any) =>
            dataSegement.name !== name
                ? dataSegement
                : dataSegement.visible
                ? { ...dataSegement, visible: false }
                : { ...dataSegement, visible: true },
        ),
    }));
};

export const titleShortener = (localization: Localization) => {
    const defaultValues = {
        [Localization.UK]: 'uk',
        [Localization.US]: 'us',
    };

    return defaultValues[localization].toUpperCase();
};

export const routerFormat = (value: string, isToQueryParam: boolean) => {
    if (isToQueryParam) {
        const routerName = lowerFirst(value);
        return routerName.replaceAll(' ', '_');
    } else {
        const tabName = upperFirst(value);
        return tabName.replaceAll('_', ' ');
    }
};
